import type { CrossSection } from "../models/MillVisualizer";

// SVG path generators for a mill roll seen from the side. Each roll is a
// rectangle with a groove (notch) carved into the rolling edge — the inner
// edge that faces the billet. The groove shape matches the cross-section the
// pair of rolls is producing, so the visual "tells" the operator what's
// being made at this stand.
//
// Top roll: groove on its BOTTOM edge.
// Bottom roll: groove on its TOP edge.
// Combined, the two grooves form the cross-section the billet is squeezed into.

const NOTCH_HALF_W_RATIO = 0.3; // total notch width = 60% of roll width
const NOTCH_DEPTH_RATIO = 0.3; // notch depth = 30% of roll height
const SQUARE_DEPTH_FACTOR = 0.7; // square notch a bit shallower than the others

export interface RollDims {
	cx: number;
	cy: number;
	width: number;
	height: number;
}

/**
 * Top roll path. Walks clockwise from top-left; the notch interrupts the
 * bottom edge. SVG arc sweep flag = 0 makes the arc bulge UP (into the roll
 * body) when traversing right-to-left along the bottom edge.
 */
export const buildTopRollPath = (
	{ cx, cy, width, height }: RollDims,
	section: CrossSection,
): string => {
	const left = cx - width / 2;
	const right = cx + width / 2;
	const top = cy - height / 2;
	const bot = cy + height / 2;
	const nhw = width * NOTCH_HALF_W_RATIO;
	const nd = height * NOTCH_DEPTH_RATIO;

	if (section === "flat") {
		return `M ${left},${top} L ${right},${top} L ${right},${bot} L ${left},${bot} Z`;
	}

	let notchPath = "";
	switch (section) {
		case "round":
			notchPath = `A ${nhw},${nhw} 0 0 0 ${cx - nhw},${bot}`;
			break;
		case "oval":
			notchPath = `A ${nhw},${nhw * 0.5} 0 0 0 ${cx - nhw},${bot}`;
			break;
		case "square":
			notchPath = `L ${cx + nhw},${bot - nd * SQUARE_DEPTH_FACTOR} L ${cx - nhw},${bot - nd * SQUARE_DEPTH_FACTOR} L ${cx - nhw},${bot}`;
			break;
		case "diamond":
			notchPath = `L ${cx},${bot - nd} L ${cx - nhw},${bot}`;
			break;
	}

	return `M ${left},${top} L ${right},${top} L ${right},${bot} L ${cx + nhw},${bot} ${notchPath} L ${left},${bot} Z`;
};

/**
 * Bottom roll path. Walks clockwise from top-left; the notch interrupts the
 * TOP edge. Arc sweep=0 traversing left-to-right along the top makes the arc
 * bulge DOWN (into the roll body).
 */
export const buildBottomRollPath = (
	{ cx, cy, width, height }: RollDims,
	section: CrossSection,
): string => {
	const left = cx - width / 2;
	const right = cx + width / 2;
	const top = cy - height / 2;
	const bot = cy + height / 2;
	const nhw = width * NOTCH_HALF_W_RATIO;
	const nd = height * NOTCH_DEPTH_RATIO;

	if (section === "flat") {
		return `M ${left},${top} L ${right},${top} L ${right},${bot} L ${left},${bot} Z`;
	}

	let notchPath = "";
	switch (section) {
		case "round":
			notchPath = `A ${nhw},${nhw} 0 0 0 ${cx + nhw},${top}`;
			break;
		case "oval":
			notchPath = `A ${nhw},${nhw * 0.5} 0 0 0 ${cx + nhw},${top}`;
			break;
		case "square":
			notchPath = `L ${cx - nhw},${top + nd * SQUARE_DEPTH_FACTOR} L ${cx + nhw},${top + nd * SQUARE_DEPTH_FACTOR} L ${cx + nhw},${top}`;
			break;
		case "diamond":
			notchPath = `L ${cx},${top + nd} L ${cx + nhw},${top}`;
			break;
	}

	return `M ${left},${top} L ${cx - nhw},${top} ${notchPath} L ${right},${top} L ${right},${bot} L ${left},${bot} Z`;
};
